import React from 'react'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { BForm, BRow, BCol, BIcon, BLink } from '@prague-digi/ui-components'

import { ReduxState } from '../../types'
import { clientActions, modalDialogActions } from '../../redux'
import messages from '../../i18n'
import {
  errorHandler,
  form,
  FormInterface,
  intl,
  IntlInterface,
  wizard,
  WizardInterface,
} from '../../hoc'

import { preformatBirthNumber } from '../../utils/helpers'
import {
  BCombobox,
  BTextInput,
  PageControlButtons,
  PrefillFormButton,
  Title,
} from '../../components/'
import validate from './ClientDetails.validate'
import * as codetableUtils from '../../utils/codetable'
import {
  API_ERROR,
  ENTER_PERSONAL_DATA,
  ID_CARD_NUMBER_HINT,
  ID_CARD_VALIDITY_HINT,
  PERSONAL_NUMBER_HINT,
} from '../../constants/modals'
import { ApiErrorInfo, Response } from '../../api/api'
import { unixTimeToCzDate } from '../../utils/dates'

interface Props extends FormInterface, WizardInterface, IntlInterface {
  countries: codetableUtils.Option[]
  isModal?: boolean
  maritalStatuses: codetableUtils.Option[]
  onClose: any
  onContinue: any
  saveClientDetails: any
  showModalDialog: any
  storeData: any
}

const FORM_NAME = 'ClientDetails'

export class ClientDetails extends React.Component<Props> {
  constructor(props: Props) {
    super(props)
    props.initialize({ ...props.storeData })
  }

  onSubmit = () => {
    this.props.handleSubmit(this.saveClientDetailsCallback)()
  }

  onBack = () => {
    if (this.props.onClose) {
      this.props.onClose()
    } else {
      this.props.wizard.gotoPrevious()
    }
  }

  saveClientDetailsCallback = () => {
    this.props.saveClientDetails({
      success: () => {
        if (this.props.onContinue) {
          this.props.onContinue()
        } else {
          this.props.wizard.gotoNext()
        }
      },
      error: (errorInfo: ApiErrorInfo) => {
        if (errorInfo.type === Response.dataError && errorInfo.errors) {
          this.props.setErrors(FORM_NAME, errorInfo.errors)
        } else {
          this.props.showModalDialog(API_ERROR({ errorInfo, onRetry: this.onSubmit }))
        }
      },
    })
  }

  showModal = (modal: Object) => () => this.props.showModalDialog(modal)

  render() {
    const { countries, isModal, invalid, maritalStatuses, translate } = this.props
    return (
      <BForm id={FORM_NAME}>
        <Title
          center={isModal}
          message={messages.clientDetails.formTitle}
          subTitle={!isModal}
          subtitleJsx={
            <>
              {translate(messages.common.subTitle)}{' '}
              <BLink onClick={this.showModal(ENTER_PERSONAL_DATA)}>
                {translate(messages.clientDetails.subTitleLink)}
              </BLink>
            </>
          }
        />

        <BRow>
          <BCol xl={6} lg={6} md={12} sm={12} xs={12}>
            <BTextInput
              name="identityCardNumber"
              messages={{
                label: (
                  <>
                    {translate(messages.clientDetails.idCardNumber)}
                    <BIcon name="info" onClick={this.showModal(ID_CARD_NUMBER_HINT)} theme="ml-2" />
                  </>
                ),
              }}
              testid="identityCardNumber"
            />
          </BCol>
          <BCol xl={6} lg={6} md={12} sm={12} xs={12}>
            <BTextInput
              name="identityCardExpiration"
              messages={{
                label: (
                  <>
                    {translate(messages.clientDetails.idCardValidity)}
                    <BIcon
                      name="info"
                      onClick={this.showModal(ID_CARD_VALIDITY_HINT)}
                      theme="ml-2"
                    />
                  </>
                ),
              }}
              testid="identityCardExpiration"
            />
          </BCol>
        </BRow>

        <BRow>
          <BCol xl={6} lg={6} md={12} sm={12} xs={12}>
            <BTextInput
              name="personalIdentificationNumber"
              messages={{
                label: (
                  <>
                    {translate(messages.clientDetails.personalNumber)}
                    <BIcon
                      name="info"
                      onClick={this.showModal(PERSONAL_NUMBER_HINT)}
                      theme="ml-2"
                    />
                  </>
                ),
              }}
              testid="personalIdentificationNumber"
            />
          </BCol>
          <BCol xl={6} lg={6} md={12} sm={12} xs={12}>
            <BCombobox
              name="citizenship"
              disabled
              data={countries}
              messages={{
                label: translate(messages.clientDetails.nationality),
              }}
              testid="citizenship"
            />
          </BCol>
        </BRow>

        <BRow>
          <BCol xl={6} lg={6} md={12} sm={12} xs={12}>
            <BTextInput
              name="placeOfBirth"
              messages={{ label: translate(messages.clientDetails.birthPlace) }}
              testid="placeOfBirth"
            />
          </BCol>

          <BCol xl={6} lg={6} md={12} sm={12} xs={12}>
            <BCombobox
              name="countryOfBirth"
              data={countries}
              messages={{ label: translate(messages.clientDetails.birthCountry) }}
              testid="countryOfBirth"
            />
          </BCol>
        </BRow>

        <BRow>
          <BCol xl={6} lg={6} md={12} sm={12} xs={12}>
            <BCombobox
              name="maritalStatus"
              data={maritalStatuses}
              messages={{ label: translate(messages.clientDetails.maritalStatus) }}
              testid="maritalStatus"
            />
          </BCol>
        </BRow>

        <PageControlButtons
          disableSubmit={invalid}
          isModal={isModal}
          onSubmit={this.onSubmit}
          secondaryAction={this.onBack}
        />

        <PrefillFormButton formName="ClientDetails" />
      </BForm>
    )
  }
}

export default compose(
  errorHandler('containers/ClientDetails/ClientDetails.tsx'),
  wizard,
  connect(
    (state: ReduxState) => ({
      maritalStatuses: codetableUtils.toOptions(state.codetable.maritalStatuses),
      countries: codetableUtils.toOptions(state.codetable.countries),
      storeData: {
        identityCardNumber: state.client.identityCardNumber,
        identityCardExpiration: unixTimeToCzDate(state.client.identityCardExpiration),
        personalIdentificationNumber: preformatBirthNumber(
          state.client.personalIdentificationNumber
        ),
        maritalStatus: state.client.maritalStatus,
        citizenship: state.client.citizenship,
        placeOfBirth: state.client.placeOfBirth,
        countryOfBirth: state.client.countryOfBirth,
      },
    }),
    {
      saveClientDetails: clientActions.saveClientDetails,
      showModalDialog: modalDialogActions.showModalDialog,
    }
  ),
  form({
    name: FORM_NAME,
    validate,
  }),
  intl
)(ClientDetails)
